import { useState } from 'react';
import { ChevronDown, Eye, RefreshCw } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { PageHeader } from '../../components/PageHeader';
import { useWebhookDeliveries } from '../../api/admin';
import { formatTime } from '../../lib/format';
import type { WebhookDelivery } from '../../mocks/admin';

/** Webhook deliveries — table with payload viewer modal. */
export function Webhooks() {
  const { data: webhookDeliveries = [] } = useWebhookDeliveries();
  const [viewing, setViewing] = useState<WebhookDelivery | null>(null);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Webhook deliveries"
        subtitle="Outbound events to external systems (Laravel POS, accounting, etc.)."
        action={<Button variant="secondary" size="sm" iconLeft={<RefreshCw className="w-4 h-4" />}>Refresh</Button>}
      />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <Filter label="Status" value="All" />
          <Filter label="Target" value="All" />
          <Filter label="Date" value="Last 24h" />
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">When</th>
                <th className="text-left font-medium px-4 py-3">Event</th>
                <th className="text-left font-medium px-4 py-3">Target</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Attempt</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {webhookDeliveries.map((w) => (
                <tr key={w.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3 tabular-nums text-zinc-700 dark:text-zinc-200">{formatTime(w.at)}</td>
                  <td className="px-4 py-3">
                    <span className="mono text-xs text-zinc-700 dark:text-zinc-200">{w.event}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">
                    <span className="mono text-xs">{w.target}</span>
                    {w.isRetry && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-300">retry</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={w.status >= 200 && w.status < 300 ? 'success' : 'error'}>
                      {w.status} {w.status >= 200 && w.status < 300 ? 'OK' : 'Failed'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{w.attempt}</td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setViewing(w)}
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        title="View payload"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {w.status >= 400 && (
                        <button
                          type="button"
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                          title="Retry"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <PayloadModal delivery={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function Filter({ label, value }: { label: string; value: string }) {
  return (
    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition">
      <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
      <span>{value}</span>
      <ChevronDown className="w-3 h-3 text-zinc-400" />
    </button>
  );
}

function PayloadModal({ delivery, onClose }: { delivery: WebhookDelivery | null; onClose: () => void }) {
  const open = delivery !== null;

  const requestPayload = delivery
    ? {
        event: delivery.event,
        delivery_id: delivery.id,
        attempt: delivery.attempt,
        timestamp: delivery.at,
        data: {
          business_id: 'biz_300',
          business_name: 'Acme Main Store',
          status: 'active',
          plan: 'Pro',
        },
      }
    : null;

  const responsePayload = delivery
    ? {
        status: delivery.status,
        headers: { 'content-type': 'application/json' },
        body: delivery.status >= 200 && delivery.status < 300 ? { received: true } : { error: 'Service Unavailable' },
        latency_ms: 412,
      }
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={delivery ? `Webhook delivery · ${delivery.event}` : ''}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="secondary" iconLeft={<RefreshCw className="w-4 h-4" />}>Re-deliver</Button>
        </>
      }
    >
      {delivery && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <Field k="Status" v={<Badge tone={delivery.status < 300 ? 'success' : 'error'}>{delivery.status}</Badge>} />
            <Field k="Attempt" v={<span className="tabular-nums">{delivery.attempt}</span>} />
            <Field k="Target" v={<span className="mono text-xs">{delivery.target}</span>} />
            <Field k="At" v={<span className="tabular-nums">{formatTime(delivery.at)}</span>} />
          </div>

          <div>
            <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">Request payload</h3>
            <pre className="text-xs mono bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 overflow-x-auto text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap break-all">
{JSON.stringify(requestPayload, null, 2)}
            </pre>
          </div>

          <div>
            <h3 className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">Response</h3>
            <pre className="text-xs mono bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 rounded-lg p-3 overflow-x-auto text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap break-all">
{JSON.stringify(responsePayload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500 dark:text-zinc-400 w-16 text-xs uppercase tracking-wider font-medium">{k}</span>
      <span className="text-zinc-900 dark:text-zinc-100">{v}</span>
    </div>
  );
}
